import React, { useMemo, useState, useEffect, useRef } from 'react';
import { ChevronUp, ChevronDown, X } from 'lucide-react';
import ContextHelpTip from '../common/ContextHelpTip';
import TieBreakModal from './TieBreakModal';
import {
  getActiveConsentItems,
  isConsentCalendarAgendaRow,
  isMainFromConsentAgendaRow,
} from '../../utils/consentAgenda';

const ACTIVE_BUSINESS_HINT =
  'Recognize motions, state the question, vote, and record the result.';

const ANNOUNCE_RESULT_HINT =
  'Use the chair script first; this button records that you declared the result and advances the meeting.';

const MotionManagement = ({ 
  theme, 
  selectedMotionType, 
  motionTypes, 
  memberName, 
  setMemberName, 
  motionText, 
  setMotionText, 
  onSubmitMotion, 
  onCancelMotion,
  currentMotion,
  motionStack = [],
  onSecondMotion,
  onStateMotion,
  onOpenDebate,
  onCloseDebate,
  onReadyForVote,
  onVoteOnMotion,
  onPutToAssembly,
  onAnnounceResult,
  remoteVoteSession = null,
  remoteVoteTally = null,
  remoteVoteBusy = false,
  remoteVoteError = null,
  onOpenRemoteVote = null,
  onCloseRemoteVote = null,
  meetingSetup,
  unanimousConsentRequests = [],
  onUnanimousConsent,
  pendingUCRequest,
  onUCGrant,
  onUCObjection,
  showAgendaWidget,
  currentAgendaIndex,
  orderOfBusiness = [],
  agendaDisplayText,
  onPrevious,
  onNext,
  onAdvanceAgenda,
  onCloseAgendaWidget,
  canAdvance,
  pullConsentLineToFloor,
  adoptConsentAgenda,
  submitMainMotionFromConsentAgenda,
  dismissConsentSuggestion,
  currentAgendaItem = null,
}) => {
  const cardClass =
    theme === 'dark'
      ? 'bg-gray-800 text-white border border-gray-600'
      : 'bg-pp-surface text-pp-ink border border-pp-line/80';
  const [tallyYes, setTallyYes] = useState('');
  const [tallyNo, setTallyNo] = useState('');
  const [tallyAbstain, setTallyAbstain] = useState('');
  const [chairResponse, setChairResponse] = useState('');
  const [mainFromConsentMember, setMainFromConsentMember] = useState('');

  // When the remote ballot transitions from open to closed, pull the final counts
  // straight into the tally fields so "Record tally" is the one next action —
  // closing the ballot should never leave the chair without the final numbers.
  const prevRemoteVoteStatusRef = useRef(remoteVoteSession?.status);
  useEffect(() => {
    const prevStatus = prevRemoteVoteStatusRef.current;
    prevRemoteVoteStatusRef.current = remoteVoteSession?.status;
    if (prevStatus === 'open' && remoteVoteSession?.status === 'closed') {
      setTallyYes(String(Number(remoteVoteTally?.counts?.aye ?? remoteVoteTally?.counts?.yes ?? 0)));
      setTallyNo(String(Number(remoteVoteTally?.counts?.no ?? 0)));
      setTallyAbstain(String(Number(remoteVoteTally?.counts?.abstain ?? 0)));
    }
  }, [remoteVoteSession?.status, remoteVoteTally]);

  const onConsentCalendar =
    currentAgendaItem &&
    isConsentCalendarAgendaRow(currentAgendaItem) &&
    !currentAgendaItem.completed;
  const activeConsentItems = onConsentCalendar ? getActiveConsentItems(meetingSetup.consentAgendaItems) : [];
  const suggestions = meetingSetup.consentRemovalSuggestions || [];
  const onMainFromConsentRow =
    currentAgendaItem &&
    isMainFromConsentAgendaRow(currentAgendaItem) &&
    !currentAgendaItem.completed;
  const showMainFromConsentIntroForm =
    onMainFromConsentRow && !currentAgendaItem.introducedMotionId && !currentMotion;
  const showMainFromConsentAfterDisposeHint =
    onMainFromConsentRow && Boolean(currentAgendaItem.introducedMotionId) && !currentMotion;

  const votingRequirementsText = useMemo(() => {
    if (!currentMotion || !motionTypes[currentMotion.type]) return null;
    if (currentMotion.chairPutToAssembly) return 'Majority vote (put to the assembly)';
    const req = motionTypes[currentMotion.type].voteRequired;
    if (!req) return null;
    if (req === 'majority') return 'Majority vote';
    if (req === '2/3') return 'Two-thirds vote';
    if (req === 'unanimous') return 'Unanimous consent';
    if (req === 'chair_decides') return 'Chair decides';
    if (req === 'none') return 'No assembly vote';
    return String(req);
  }, [currentMotion, motionTypes]);

  const handleSecondMotion = () => {
    const name = document.getElementById('second-name').value;
    onSecondMotion(name || ''); // Allow empty name
    document.getElementById('second-name').value = '';
  };

  const [tieBreakOpen, setTieBreakOpen] = useState(false);
  const [voiceTieOpen, setVoiceTieOpen] = useState(false);

  const submitVoiceTie = (chairVote) => {
    onVoteOnMotion({ voiceTie: true, chairTieVote: chairVote });
    setVoiceTieOpen(false);
  };

  const submitTally = () => {
    const yes = Number(tallyYes || 0);
    const no = Number(tallyNo || 0);
    const abstain = Number(tallyAbstain || 0);
    if (yes === no && yes > 0) {
      setTieBreakOpen(true);
      return;
    }
    onVoteOnMotion({ yes, no, abstain });
    setTallyYes('');
    setTallyNo('');
    setTallyAbstain('');
  };

  const resolveTieBreak = (chairVote) => {
    const yes = Number(tallyYes || 0);
    const no = Number(tallyNo || 0);
    const abstain = Number(tallyAbstain || 0);
    const finalYes = chairVote === 'yes' ? yes + 1 : yes;
    const finalNo = chairVote === 'no' ? no + 1 : no;
    onVoteOnMotion({
      yes: finalYes,
      no: finalNo,
      abstain,
      chairBrokeTie: chairVote !== 'decline',
      chairTieVote: chairVote,
    });
    setTieBreakOpen(false);
    setTallyYes('');
    setTallyNo('');
    setTallyAbstain('');
  };

  const tallyIsTied = Number(tallyYes || 0) === Number(tallyNo || 0) && Number(tallyYes || 0) > 0;

  const getChairResolutionLabels = (motionKey) => {
    if (motionKey === 'POINT_ORDER') {
      return { pass: 'Well taken', fail: 'Not well taken' };
    }
    if (motionKey === 'APPEAL') {
      return { pass: 'Sustained', fail: 'Overruled' };
    }
    return { pass: 'Sustained', fail: 'Overruled' };
  };


  return (
    <div className={`min-h-[12rem] flex-1 overflow-y-auto rounded-xl p-3 shadow-pp ${cardClass}`} data-tour="active-business">
      <div className="flex items-center gap-1.5">
        <h2 className={`text-sm font-semibold tracking-tight ${theme === 'dark' ? 'text-white' : 'text-pp-ink'}`}>
          Active business
        </h2>
        <ContextHelpTip text={ACTIVE_BUSINESS_HINT} theme={theme === 'dark' ? 'dark' : 'light'} />
      </div>

      {/* Agenda Widget */}
      {showAgendaWidget && orderOfBusiness.length > 0 && (
        <div
          className="mb-3 mt-3 rounded-lg border border-pp-sage/30 bg-pp-sageSoft p-2.5"
          data-tour="agenda-strip"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-pp-sage">Agenda</span>
              <span className="truncate text-sm font-medium text-pp-ink">
                {currentAgendaIndex + 1}. {agendaDisplayText}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {currentAgendaIndex > 0 && (
                <button
                  onClick={onPrevious}
                  className="rounded-lg p-1 text-pp-sage hover:bg-white/60"
                  title="Previous agenda item"
                >
                  <ChevronUp size={16} />
                </button>
              )}
              {currentAgendaIndex < orderOfBusiness.length - 1 && (
                <button
                  onClick={onNext}
                  className="rounded-lg p-1 text-pp-sage hover:bg-white/60"
                  title="Next agenda item"
                >
                  <ChevronDown size={16} />
                </button>
              )}
              {canAdvance && (
                <button
                  data-tour="agenda-complete"
                  onClick={onAdvanceAgenda}
                  className="rounded-lg bg-pp-sage px-2 py-1 text-xs font-semibold text-white hover:opacity-90"
                >
                  Complete
                </button>
              )}
              <button
                onClick={onCloseAgendaWidget}
                className="rounded-lg p-1 text-pp-muted hover:bg-white/60"
                title="Close agenda widget"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {onConsentCalendar && pullConsentLineToFloor && adoptConsentAgenda && (
        <div className="mb-4 rounded-xl border-2 border-emerald-400/60 bg-emerald-50/90 p-4">
          <h3 className="mb-1 text-sm font-semibold text-emerald-950">Consent calendar</h3>
          <p className="mb-3 text-xs text-emerald-900/90">
            Remove items to the floor if a member asks for separate consideration. When ready, adopt the remaining
            items by general consent.
          </p>
          {activeConsentItems.length === 0 ? (
            <p className="mb-3 text-sm text-emerald-900/80">
              No items remain on the consent calendar
              {(meetingSetup.consentPulledToFloorQueue || []).length > 0
                ? ' (all listed items were removed to the floor).'
                : '.'}{' '}
              You can still adopt to complete this agenda segment and queue main motions from any pulls.
            </p>
          ) : (
            <ul className="mb-3 max-h-52 space-y-2 overflow-y-auto">
              {activeConsentItems.map((item) => (
                <li
                  key={item.id}
                  className="flex items-start justify-between gap-2 rounded-lg border border-emerald-200 bg-white p-2"
                >
                  <span className="min-w-0 flex-1 text-sm text-pp-ink">{item.text || '(empty line)'}</span>
                  <button
                    type="button"
                    onClick={() => pullConsentLineToFloor(item.id)}
                    className="shrink-0 rounded-md bg-amber-600 px-2 py-1 text-xs font-semibold text-white hover:bg-amber-700"
                  >
                    Remove to floor
                  </button>
                </li>
              ))}
            </ul>
          )}
          {suggestions.length > 0 && dismissConsentSuggestion && (
            <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50/90 p-2">
              <div className="mb-1 text-xs font-semibold text-amber-900">Observer / audience suggested removals</div>
              <ul className="space-y-1">
                {suggestions.map((s) => (
                  <li key={s.id} className="flex items-start justify-between gap-2 text-xs text-amber-950">
                    <span>
                      <span className="font-medium">{s.suggestedAt}:</span> {s.text || s.lineId}
                    </span>
                    <button
                      type="button"
                      onClick={() => dismissConsentSuggestion(s.id)}
                      className="shrink-0 text-amber-800 underline hover:text-amber-950"
                    >
                      Dismiss
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <button
            type="button"
            disabled={Boolean(currentMotion)}
            onClick={() => adoptConsentAgenda()}
            className="w-full rounded-lg bg-emerald-700 px-3 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Adopt consent agenda (general consent)
          </button>
          {currentMotion && (
            <p className="mt-2 text-center text-xs text-emerald-900/80">
              Dispose of or withdraw the pending motion before adopting the consent calendar.
            </p>
          )}
        </div>
      )}

      {showMainFromConsentAfterDisposeHint && (
        <div className="mb-4 rounded-xl border border-pp-sage/40 bg-pp-sageSoft p-4">
          <h3 className="mb-1 text-sm font-semibold text-pp-ink">Consent-calendar main motion — complete</h3>
          <p className="text-sm text-pp-ink/90">
            The vote has been announced. When you are ready to leave this agenda item, use{' '}
            <strong className="font-semibold">Complete</strong> on the agenda strip above to move to the next item.
          </p>
        </div>
      )}

      {showMainFromConsentIntroForm && submitMainMotionFromConsentAgenda && (
        <div className="mb-4 rounded-xl border border-pp-accent/40 bg-pp-accentSoft p-4">
          <h3 className="mb-1 text-sm font-semibold text-pp-ink">Main motion (from consent calendar)</h3>
          <p className="mb-2 text-xs text-pp-muted">Agenda text — record who moves it, then run debate and vote as usual.</p>
          <div className="mb-2 rounded-md border border-pp-line bg-pp-surface p-2 text-sm font-medium text-pp-ink">
            {currentAgendaItem.mainMotionText || '(no text)'}
          </div>
          <input
            type="text"
            placeholder="Member moving the motion"
            value={mainFromConsentMember}
            onChange={(e) => setMainFromConsentMember(e.target.value)}
            className="mb-2 w-full rounded-lg border border-pp-line bg-pp-surface p-2 text-sm"
          />
          <button
            type="button"
            onClick={() => {
              submitMainMotionFromConsentAgenda(
                currentAgendaItem.mainMotionText || '',
                mainFromConsentMember
              );
              setMainFromConsentMember('');
            }}
            className="w-full rounded-lg bg-pp-ink px-3 py-2 text-sm font-semibold text-pp-surface hover:opacity-90"
          >
            Record main motion
          </button>
        </div>
      )}

      {/* Pending Unanimous Consent Request */}
      {pendingUCRequest && (
        <div className="mb-4 p-4 bg-yellow-50 rounded-lg border-2 border-yellow-300">
          <h3 className="font-medium mb-3 text-yellow-800">Unanimous Consent Request Pending</h3>
          <div className="space-y-3">
            <p className="text-sm text-yellow-700 bg-yellow-100 p-2 rounded">
              "{pendingUCRequest.text}"
            </p>
            <p className="text-xs text-yellow-600">
              Requested at {pendingUCRequest.timestamp}
            </p>
            <div className="flex gap-3">
              <button
                onClick={onUCGrant}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded font-medium hover:bg-green-700 transition-colors"
              >
                ✓ Grant UC (No Objection)
              </button>
              <button
                onClick={onUCObjection}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded font-medium hover:bg-red-700 transition-colors"
              >
                ✗ Object (Regular Process)
              </button>
            </div>
          </div>
        </div>
      )}


      {selectedMotionType && (
        <div className="mb-4 rounded-xl border border-pp-line bg-pp-paper/70 p-4">
          <h3 className="mb-2 text-base font-semibold tracking-tight text-pp-ink">
            New {motionTypes[selectedMotionType].name}
          </h3>
          <div className="space-y-2">
            <input
              type="text"
              placeholder="Member name"
              value={memberName}
              onChange={(e) => setMemberName(e.target.value)}
              className="w-full rounded-lg border border-pp-line bg-pp-surface p-2 text-sm"
            />
            {(selectedMotionType === 'MAIN') && (
              <textarea
                placeholder="Motion text"
                value={motionText}
                onChange={(e) => setMotionText(e.target.value)}
                className="h-20 w-full rounded-lg border border-pp-line bg-pp-surface p-2 text-sm"
              />
            )}
            {(selectedMotionType === 'AMEND') && (
              <textarea
                placeholder="Amendment text (e.g., 'to strike out the words...' or 'to insert after the word...')"
                value={motionText}
                onChange={(e) => setMotionText(e.target.value)}
                className="h-20 w-full rounded-lg border border-pp-line bg-pp-surface p-2 text-sm"
              />
            )}
            {(selectedMotionType === 'POINT_ORDER') && (
              <textarea
                placeholder="What rule or procedure was violated? (optional)"
                value={motionText}
                onChange={(e) => setMotionText(e.target.value)}
                className="h-20 w-full rounded-lg border border-pp-line bg-pp-surface p-2 text-sm"
              />
            )}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onSubmitMotion}
                className="rounded-lg bg-pp-sage px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
              >
                {selectedMotionType === 'POINT_ORDER' ? 'Raise point of order' : 'Make motion'}
              </button>
              <button
                type="button"
                onClick={() => onCancelMotion(false)}
                className="rounded-lg border border-pp-line bg-pp-surface px-3 py-2 text-sm font-medium text-pp-muted hover:bg-pp-paper"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Current Motion */}
      {currentMotion && (
        <div className={`mb-3 rounded-xl border border-pp-line/80 p-4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-pp-paper/50'}`}>
          <h4 className={`mb-2 text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-pp-ink'}`}>
            Current: {motionTypes[currentMotion.type].name}
          </h4>
          <p className="text-sm mb-2">"{currentMotion.text}"</p>
          <p className={`text-xs mb-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            By {currentMotion.member} at {currentMotion.timestamp}
          </p>

          <div className="space-y-2">
            {currentMotion.status === 'pending_second' && (
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Seconded by (optional)"
                  id="second-name"
                  className="flex-1 p-1 border rounded text-xs"
                />
                <button
                  type="button"
                  onClick={handleSecondMotion}
                  className="rounded-lg bg-pp-ink px-2 py-1 text-xs font-semibold text-pp-surface hover:opacity-90"
                >
                  Second
                </button>
                <button
                  type="button"
                  onClick={() => onCancelMotion(true)}
                  className="rounded-lg bg-red-600 px-2 py-1 text-xs font-semibold text-white hover:bg-red-700"
                >
                  No second
                </button>
              </div>
            )}

            {currentMotion.status === 'seconded' && (
              <button
                type="button"
                onClick={onStateMotion}
                className="w-full rounded-lg bg-pp-accent px-3 py-2 text-sm font-semibold text-white hover:bg-pp-accentHover"
              >
                State the question (debate open)
              </button>
            )}

            {currentMotion.status === 'stated' && (
              <div className="space-y-2">
                <p className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-pp-muted'}`}>
                  {motionTypes[currentMotion.type]?.debatable
                    ? 'The question has been stated. Open debate, or put the question when ready.'
                    : 'The question has been stated. This motion is not debatable, but a member may still move to amend it. Put the question when ready.'}
                </p>
                {motionTypes[currentMotion.type]?.debatable && onOpenDebate && (
                  <button
                    type="button"
                    onClick={onOpenDebate}
                    className="w-full rounded-lg border border-pp-accent/40 bg-pp-accentSoft px-3 py-2 text-sm font-semibold text-pp-accent hover:bg-pp-paper"
                  >
                    Open debate
                  </button>
                )}
                <button
                  type="button"
                  onClick={onReadyForVote}
                  className="w-full rounded-lg bg-pp-sage px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
                >
                  Put the question
                </button>
              </div>
            )}

            {currentMotion.status === 'open_debate' && (
              <button
                type="button"
                onClick={onCloseDebate}
                className="w-full rounded-lg border border-pp-accent/40 bg-pp-accentSoft px-3 py-2 text-sm font-semibold text-pp-accent hover:bg-pp-paper"
              >
                Close debate
              </button>
            )}

            {currentMotion.status === 'close_debate' && (
              <button
                type="button"
                onClick={onReadyForVote}
                className="w-full rounded-lg bg-pp-sage px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
              >
                Put the question
              </button>
            )}

            {currentMotion.status === 'ready_for_vote' && (
              <div className="space-y-2">
                <div className="rounded-xl border border-pp-line bg-pp-surface p-3">
                  {motionTypes[currentMotion.type]?.flowType === 'INFO' ? (
                    <>
                      <div className="mb-2 text-xs font-medium text-gray-700">Chair’s response</div>
                      <textarea
                        placeholder="Enter the chair’s response (optional)"
                        value={chairResponse}
                        onChange={(e) => setChairResponse(e.target.value)}
                        className="mb-3 h-20 w-full rounded-lg border border-pp-line bg-pp-surface p-2 text-sm"
                      />
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            // Persist response onto the motion so minutes/export can reference it later if needed.
                            // Note: current architecture stores motionData snapshots in minutes; this keeps UI simple.
                            onVoteOnMotion('passed');
                          }}
                          className="flex-1 rounded-lg bg-pp-ink px-3 py-2 text-sm font-semibold text-pp-surface hover:opacity-90"
                        >
                          Close inquiry
                        </button>
                      </div>
                    </>
                  ) : motionTypes[currentMotion.type]?.flowType === 'CHAIR' &&
                    currentMotion.type !== 'APPEAL' &&
                    !currentMotion.chairPutToAssembly ? (
                    <>
                      <div className="mb-2 text-xs font-medium text-gray-700">Chair’s ruling</div>
                      {votingRequirementsText && (
                        <div className="mb-3 text-xs text-gray-500">{votingRequirementsText}</div>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => onVoteOnMotion('passed')}
                          className="flex-1 rounded-lg bg-pp-ink px-3 py-2 text-sm font-semibold text-pp-surface hover:opacity-90"
                        >
                          {getChairResolutionLabels(currentMotion.type).pass}
                        </button>
                        <button
                          type="button"
                          onClick={() => onVoteOnMotion('failed')}
                          className="rounded-lg border border-pp-line bg-pp-paper px-3 py-2 text-sm text-pp-ink hover:bg-pp-surface"
                        >
                          {getChairResolutionLabels(currentMotion.type).fail}
                        </button>
                        {currentMotion.type === 'POINT_ORDER' && onPutToAssembly && (
                          <button
                            type="button"
                            onClick={onPutToAssembly}
                            className="rounded-lg border border-pp-line bg-pp-surface px-3 py-2 text-sm text-pp-ink hover:bg-pp-paper"
                            title="Let the assembly vote on whether the point is well taken, instead of the chair ruling alone"
                          >
                            Put to the assembly
                          </button>
                        )}
                      </div>
                    </>
                  ) : motionTypes[currentMotion.type]?.voteRequired === 'none' ? (
                    <>
                      <div className="mb-2 text-xs font-medium text-gray-700">Dispose of the question</div>
                      {votingRequirementsText && (
                        <div className="mb-3 text-xs text-gray-500">{votingRequirementsText}</div>
                      )}
                      <p className="mb-3 text-xs text-gray-600">
                        RONR: no assembly vote — record how the chair disposed (e.g. orders sustained or not).
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => onVoteOnMotion('passed')}
                          className="flex-1 rounded-lg bg-pp-ink px-3 py-2 text-sm font-semibold text-pp-surface hover:opacity-90"
                        >
                          Record disposed (sustained)
                        </button>
                        <button
                          type="button"
                          onClick={() => onVoteOnMotion('failed')}
                          className="rounded-lg border border-pp-line bg-pp-paper px-3 py-2 text-sm text-pp-ink hover:bg-pp-surface"
                        >
                          Record not sustained
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-medium text-gray-700">Tally vote</div>
                        {votingRequirementsText && (
                          <div className="text-xs text-gray-500">{votingRequirementsText}</div>
                        )}
                      </div>

                      {/* Voice vote — the common case: no count needed */}
                      <div className="mb-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => onVoteOnMotion('passed')}
                          className="flex-1 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                          title="Voice vote — no count needed"
                        >
                          {currentMotion.type === 'POINT_ORDER'
                            ? 'Well taken'
                            : currentMotion.type === 'APPEAL'
                              ? 'Sustained'
                              : 'Pass'}
                        </button>
                        <button
                          type="button"
                          onClick={() => onVoteOnMotion('failed')}
                          className="flex-1 rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700"
                          title="Voice vote — no count needed"
                        >
                          {currentMotion.type === 'POINT_ORDER'
                            ? 'Not well taken'
                            : currentMotion.type === 'APPEAL'
                              ? 'Overruled'
                              : 'Fail'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setVoiceTieOpen(true)}
                          className="rounded-lg border border-amber-400 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
                          title="The chair perceives the voice vote was evenly divided"
                        >
                          Tie
                        </button>
                      </div>
                      <TieBreakModal
                        open={voiceTieOpen}
                        hasCounts={false}
                        onCancel={() => setVoiceTieOpen(false)}
                        onResolve={submitVoiceTie}
                      />

                      {onOpenRemoteVote && (
                        <div className="mb-3 rounded-lg border border-pp-line bg-pp-paper p-2.5">
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <div className="text-xs font-medium text-gray-700">Remote vote</div>
                            {remoteVoteSession?.status === 'open' ? (
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                                Open · {remoteVoteTally?.total ?? 0} ballots
                              </span>
                            ) : remoteVoteSession?.status === 'closed' ? (
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                Closed · {remoteVoteTally?.total ?? 0} ballots
                              </span>
                            ) : null}
                          </div>
                          {remoteVoteSession?.status === 'open' || remoteVoteSession?.status === 'closed' ? (
                            <div className="mb-2 grid grid-cols-3 gap-1 text-center text-xs">
                              <div className="rounded bg-pp-surface py-1">
                                Aye <span className="font-semibold">{remoteVoteTally?.counts?.aye ?? remoteVoteTally?.counts?.yes ?? 0}</span>
                              </div>
                              <div className="rounded bg-pp-surface py-1">
                                No <span className="font-semibold">{remoteVoteTally?.counts?.no ?? 0}</span>
                              </div>
                              <div className="rounded bg-pp-surface py-1">
                                Abs <span className="font-semibold">{remoteVoteTally?.counts?.abstain ?? 0}</span>
                              </div>
                            </div>
                          ) : null}
                          {remoteVoteSession?.status === 'closed' ? (
                            <p className="mb-2 text-[11px] text-pp-muted">
                              Ballot closed — final tally is filled in below. Use "Record tally" to announce the result.
                            </p>
                          ) : null}
                          {remoteVoteError ? (
                            <p className="mb-2 text-[11px] text-rose-600">{remoteVoteError}</p>
                          ) : null}
                          <div className="flex flex-wrap gap-1.5">
                            {remoteVoteSession?.status === 'open' ? (
                              <button
                                type="button"
                                disabled={remoteVoteBusy}
                                onClick={() => onCloseRemoteVote?.()}
                                className="w-full rounded-lg bg-pp-accent px-2 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
                              >
                                Close ballot
                              </button>
                            ) : remoteVoteSession?.status === 'closed' ? (
                              <>
                                <button
                                  type="button"
                                  disabled={remoteVoteBusy}
                                  onClick={() => {
                                    setTallyYes(String(Number(remoteVoteTally?.counts?.aye ?? remoteVoteTally?.counts?.yes ?? 0)));
                                    setTallyNo(String(Number(remoteVoteTally?.counts?.no ?? 0)));
                                    setTallyAbstain(String(Number(remoteVoteTally?.counts?.abstain ?? 0)));
                                  }}
                                  className="flex-1 rounded-lg bg-pp-sage px-2 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
                                >
                                  Use final tally
                                </button>
                                <button
                                  type="button"
                                  disabled={remoteVoteBusy}
                                  onClick={() => onOpenRemoteVote?.()}
                                  className="rounded-lg border border-pp-line bg-pp-surface px-2 py-1.5 text-xs text-pp-ink hover:bg-white disabled:opacity-50"
                                  title="Start a new remote ballot for this motion"
                                >
                                  Reopen new ballot
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                disabled={remoteVoteBusy}
                                onClick={() => onOpenRemoteVote?.()}
                                className="w-full rounded-lg bg-pp-accent px-2 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
                              >
                                Open remote vote
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-3 gap-2 mb-2">
                        <input
                          inputMode="numeric"
                          placeholder="Aye"
                          value={tallyYes}
                          onChange={(e) => setTallyYes(e.target.value.replace(/[^\d]/g, ''))}
                          className="w-full rounded-lg border border-pp-line bg-pp-surface p-2 text-sm"
                        />
                        <input
                          inputMode="numeric"
                          placeholder="No"
                          value={tallyNo}
                          onChange={(e) => setTallyNo(e.target.value.replace(/[^\d]/g, ''))}
                          className="w-full rounded-lg border border-pp-line bg-pp-surface p-2 text-sm"
                        />
                        <input
                          inputMode="numeric"
                          placeholder="Abstain"
                          value={tallyAbstain}
                          onChange={(e) => setTallyAbstain(e.target.value.replace(/[^\d]/g, ''))}
                          className="w-full rounded-lg border border-pp-line bg-pp-surface p-2 text-sm"
                        />
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={submitTally}
                          className="flex-1 rounded-lg bg-pp-ink px-3 py-2 text-sm font-semibold text-pp-surface hover:opacity-90"
                        >
                          Record tally
                        </button>
                        {tallyIsTied && (
                          <button
                            type="button"
                            onClick={() => setTieBreakOpen(true)}
                            className="rounded-lg border border-amber-400 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
                          >
                            Tie — chair votes
                          </button>
                        )}
                      </div>
                      <TieBreakModal
                        open={tieBreakOpen}
                        hasCounts
                        yes={Number(tallyYes || 0)}
                        no={Number(tallyNo || 0)}
                        onCancel={() => setTieBreakOpen(false)}
                        onResolve={resolveTieBreak}
                      />
                    </>
                  )}
                </div>

              </div>
            )}

            {(currentMotion.status === 'voted_passed' || currentMotion.status === 'voted_failed') && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onAnnounceResult}
                  className="min-w-0 flex-1 rounded-lg bg-pp-sage px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
                >
                  Announce result
                </button>
                <ContextHelpTip
                  className="shrink-0"
                  text={ANNOUNCE_RESULT_HINT}
                  theme={theme === 'dark' ? 'dark' : 'light'}
                />
              </div>
            )}

            {/* Small UC interface - only show when no pending request and motion can have UC */}
            {!pendingUCRequest && (currentMotion.status === 'stated' || currentMotion.status === 'open_debate' || currentMotion.status === 'close_debate' || currentMotion.status === 'ready_for_vote') && (
              <div className="mt-3 border-t border-pp-line pt-3">
                <button
                  type="button"
                  onClick={() =>
                    onUnanimousConsent &&
                    onUnanimousConsent(
                      currentMotion.type === 'AMEND'
                        ? `adopt the amendment: ${currentMotion.text}`
                        : `adopt the motion: ${currentMotion.text}`,
                      currentMotion
                    )
                  }
                  className="w-full rounded-lg border border-pp-line bg-pp-paper px-2 py-2 text-xs font-medium text-pp-ink hover:bg-pp-surface"
                >
                  Ask unanimous consent
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MotionManagement;